import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { crud } from '@/common/crud';
import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { BN_AuthRegister, BN_AuthRegisterLedgerItem } from '@/fhirworks';

const { postTransaction } = useWorkflow();

/**
 * Determine whether a BN_AuthRegister is exhausted based on its totals struct.
 *
 * A dimension (units / dollar amount) is only considered "tracked" when its
 * base value was set (the auth was created with a limit in that dimension).
 * An untracked dimension's `current*` is stuck at the initial 0 and must not
 * trigger exhaustion on its own.
 *
 * @param {Object} totalsObj
 * @param {number} [totalsObj.baseUnit] - units limit; falsy/undefined when untracked
 * @param {number} [totalsObj.baseAmount] - dollar limit; falsy/undefined when untracked
 * @param {number} totalsObj.currentUnit - remaining units (init 0)
 * @param {number} totalsObj.currentAmount - remaining dollars (init 0)
 * @returns {boolean} true only when every tracked dimension has reached <= 0
 */
export function isAuthRegisterExhausted(totalsObj) {
    const unitTracked = !!totalsObj?.baseUnit;
    const amountTracked = !!totalsObj?.baseAmount;
    if (!unitTracked && !amountTracked) return false;
    if (unitTracked && totalsObj.currentUnit > 0) return false;
    if (amountTracked && totalsObj.currentAmount > 0) return false;
    return true;
}

async function getChargeItemAuths(patients, services, procedures, chargeItem, isNew = false, existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (services?.length) {
        for (const patient of patients) {
            if (!patient?.id) continue;
            let claimResponseResult = null;
            let coverageResult = null;

            try {
                coverageResult = await $httpFhirApi.get(`/Coverage?beneficiary=${patient.id}&status=active`);
            } catch (error) {
                console.error('Error fetching Coverage for patient', patient.id, error);
            }

            try {
                claimResponseResult = await $httpFhirApi.get(`/ClaimResponse?patient=${patient.id}&use=preauthorization&status=active`);
            } catch (error) {
                console.error('Error fetching ClaimResponse for patient', patient.id, error);
            }

            if (claimResponseResult?.data?.length > 0 && coverageResult?.data?.length > 0) {
                const activeCoveragePayorUris = new Set(coverageResult.data.map((coverage) => coverage.payor?.[0]?.uri).filter(Boolean));
                const sortedClaimResponse = claimResponseResult.data
                    .filter((claimResponse) => activeCoveragePayorUris.has(claimResponse?.insurer?.uri))
                    .sort((a, b) => {
                        const aStart = a.preAuthPeriod?.start || a.created;
                        const bStart = b.preAuthPeriod?.start || b.created;
                        return new Date(aStart) - new Date(bStart);
                    });

                if (sortedClaimResponse.length === 0) {
                    continue;
                }

                const existingRegisters = await $httpFhirApi.get(`/BN_AuthRegister?patient=${patient.id}&auth=${sortedClaimResponse.map((claimResponse) => claimResponse.id).join(',')}`);
                const serviceIds = new Set((Array.isArray(services) ? services : [services]).map((s) => s?.id).filter(Boolean));
                const authTransaction = await calculateAuths(sortedClaimResponse, patient, existingRegisters.data, chargeItem, isNew, serviceIds);
                transaction.push(...authTransaction);
            }
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

/**
 * Build transaction entries that deduct a ChargeItem's usage from BN_AuthRegister
 * ledgers, creating or updating registers as needed.
 *
 * @param {Object[]} sortedClaimResponse - ClaimResponse resources (pre-sorted by date) whose items may authorize the charge
 * @param {{ id: string, resourceType: 'Patient' }} patient - Patient reference for register lookup
 * @param {Object[]} existingRegisters - Already-fetched BN_AuthRegister resources for this patient + auth set
 * @param {Object} chargeItem - The ChargeItem being authorized (must include code, quantity, priceOverride)
 * @param {boolean} [isNew=false] - True when the ChargeItem hasn't been persisted yet (affects supportingInformation handling)
 * @param {Set<string>} [serviceIds=new Set()] - HealthcareService IDs to match against ClaimResponse items; falls back to display-name matching when empty
 * @returns {Promise<Object[]>} FHIR transaction entries (crud.post / crud.put / crud.delete) ready for postTransaction
 */
async function calculateAuths(sortedClaimResponse, patient, existingRegisters, chargeItem, isNew = false, serviceIds = new Set()) {
    let transaction = [];

    for (const claimResponse of sortedClaimResponse) {
        // Find all existing registers for this patient + claimResponse combination.
        // Duplicates can arise from prior runs; keep the one with the most ledger
        // entries and delete the rest so tracking converges on a single register.
        const matchingRegisters = existingRegisters.filter((e) => e.auth?.id === claimResponse.id && e.patient?.id === patient.id);
        let existingRegister = matchingRegisters.sort((a, b) => (b.ledger?.length || 0) - (a.ledger?.length || 0))[0] || null;

        // Delete duplicate registers
        for (const dup of matchingRegisters.slice(1)) {
            if (dup.id) {
                transaction.push(crud.delete({ id: dup.id, resourceType: 'BN_AuthRegister' }));
            }
        }

        let newLedgerEntries = [];

        // If no existing register, initialize with auth totals from ALL items in this ClaimResponse
        if (!existingRegister?.ledger?.length) {
            for (const item of claimResponse.item) {
                const authUnits = item.authorizedItemDetail?.quantity?.value;
                const authAmount = item.authorizedItemDetail?.unitPrice?.value;

                if (authAmount !== undefined) {
                    newLedgerEntries.push(
                        new BN_AuthRegisterLedgerItem({
                            date: new Date().toISOString(),
                            amount: { value: authAmount },
                            unit: null,
                        }),
                    );
                }
                if (authUnits !== undefined) {
                    newLedgerEntries.push(
                        new BN_AuthRegisterLedgerItem({
                            date: new Date().toISOString(),
                            amount: null,
                            unit: authUnits,
                        }),
                    );
                }
            }
        }

        // Process items that match our current charge item
        for (const item of claimResponse.item) {
            const authorizedCoding = item.authorizedItemDetail?.productOrService?.coding?.[0];
            const chargeItemCoding = chargeItem?.code?.coding?.[0];
            // Match by HealthcareService ID (coding.code) when available, fall back to display name
            const matchById = authorizedCoding?.code && serviceIds.size > 0 && serviceIds.has(authorizedCoding.code);
            const matchByDisplay = authorizedCoding?.display && chargeItemCoding?.display && authorizedCoding.display === chargeItemCoding.display;

            if (!matchById && !matchByDisplay) continue;

            // Link ClaimResponse to ChargeItem via supportingInformation (not direct authNumber)
            if (!isNew && item.preAuthNumber) {
                // For existing ChargeItems, ensure ClaimResponse is in supportingInformation
                const claimResponseRef = {
                    id: claimResponse.id,
                    resourceType: 'ClaimResponse',
                };

                // Check if ClaimResponse is already in supportingInformation
                const hasClaimResponse = chargeItem.supportingInformation?.some((ref) => ref.resourceType === 'ClaimResponse' && ref.id === claimResponse.id);

                if (!hasClaimResponse) {
                    // Clone the chargeItem and update supportingInformation
                    const updatedChargeItem = { ...chargeItem };
                    updatedChargeItem.supportingInformation = [...(chargeItem.supportingInformation || []), claimResponseRef];

                    // Use crud.patch for efficient PATCH operation
                    const patchPayload = crud.patch(updatedChargeItem, chargeItem);
                    if (patchPayload) {
                        transaction.push(patchPayload);
                    }
                }
            }

            if (isNew && item.preAuthNumber) {
                // For new ChargeItems, add ClaimResponse to supportingInformation
                const claimResponseRef = {
                    id: claimResponse.id,
                    resourceType: 'ClaimResponse',
                };

                if (!chargeItem.supportingInformation) {
                    chargeItem.supportingInformation = [];
                }

                // Check if ClaimResponse is already in supportingInformation
                const hasClaimResponse = chargeItem.supportingInformation.some((ref) => ref.resourceType === 'ClaimResponse' && ref.id === claimResponse.id);

                if (!hasClaimResponse) {
                    chargeItem.supportingInformation.push(claimResponseRef);
                }
            }

            const unitsUsed = chargeItem?.quantity?.value || 1;
            const baseCharge = chargeItem?.priceOverride?.value || 0;
            const deduction = Math.round(baseCharge * unitsUsed);

            const authUnits = item.authorizedItemDetail?.quantity?.value;
            const authAmount = item.authorizedItemDetail?.unitPrice?.value;

            // Add deduction entries for this specific charge item
            if (authUnits !== undefined) {
                newLedgerEntries.push(
                    new BN_AuthRegisterLedgerItem({
                        date: new Date().toISOString(),
                        amount: null,
                        unit: -unitsUsed,
                        chargeItem: { id: chargeItem.id, resourceType: 'ChargeItem' },
                    }),
                );
            }

            if (authAmount !== undefined) {
                newLedgerEntries.push(
                    new BN_AuthRegisterLedgerItem({
                        date: new Date().toISOString(),
                        amount: { value: -deduction },
                        unit: null,
                        chargeItem: { id: chargeItem.id, resourceType: 'ChargeItem' },
                    }),
                );
            }
        }

        let authRegisterEntry;
        if (existingRegister?.id) {
            // Skip if no new entries to add — avoids overwriting changes from a
            // prior call in the same transaction that used the same API snapshot.
            if (newLedgerEntries.length === 0) {
                continue;
            }

            // Wrap the raw API data in a model FIRST, then push new entries.
            // This avoids double-wrapping BN_AuthRegisterLedgerItem instances
            // that would corrupt their data during re-construction.
            const registerModel = new BN_AuthRegister(existingRegister);
            for (const entry of newLedgerEntries) {
                registerModel.ledger.push(entry);
            }

            let totalsObj = {
                baseUnit: undefined,
                baseAmount: undefined,
                currentUnit: 0,
                currentAmount: 0,
            };

            registerModel.ledger.forEach((ledgerItem) => {
                if (ledgerItem.chargeItem) {
                    if (ledgerItem.unit) {
                        totalsObj.currentUnit = (totalsObj.currentUnit || 0) + ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.currentAmount = (totalsObj.currentAmount || 0) + ledgerItem.amount?.value;
                    }
                } else {
                    if (ledgerItem.unit) {
                        totalsObj.baseUnit = ledgerItem.unit;
                        totalsObj.currentUnit = ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.baseAmount = ledgerItem.amount?.value;
                        totalsObj.currentAmount = ledgerItem.amount?.value;
                    }
                }
            });

            registerModel.exhausted = isAuthRegisterExhausted(totalsObj);

            authRegisterEntry = crud.put(registerModel);
        } else {
            const authRegisterToSave = new BN_AuthRegister({
                patient: { id: patient.id, resourceType: 'Patient' },
                auth: { id: claimResponse.id, resourceType: 'ClaimResponse' },
                ledger: newLedgerEntries,
            });

            // Calculate totals for new register
            let totalsObj = {
                baseUnit: undefined,
                baseAmount: undefined,
                currentUnit: 0,
                currentAmount: 0,
            };

            newLedgerEntries.forEach((ledgerItem) => {
                if (ledgerItem.chargeItem) {
                    if (ledgerItem.unit) {
                        totalsObj.currentUnit = (totalsObj.currentUnit || 0) + ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.currentAmount = (totalsObj.currentAmount || 0) + ledgerItem.amount?.value;
                    }
                } else {
                    if (ledgerItem.unit) {
                        totalsObj.baseUnit = ledgerItem.unit;
                        totalsObj.currentUnit = ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.baseAmount = ledgerItem.amount?.value;
                        totalsObj.currentAmount = ledgerItem.amount?.value;
                    }
                }
            });

            authRegisterToSave.exhausted = isAuthRegisterExhausted(totalsObj);

            authRegisterEntry = crud.post(authRegisterToSave);
        }
        transaction.push(authRegisterEntry);
    }

    return transaction;
}
const extractAuthRegisters = (response) => {
    let resources = [];
    if (response.data && response.data.entry && response.data.entry.length > 0) {
        resources = response.data.entry.map((entry) => entry.resource);
    } else if (response.data && Array.isArray(response.data)) {
        resources = response.data;
    } else if (response.data && response.data.resourceType) {
        resources = [response.data];
    }
    return resources;
};

async function removeAuthTrackingForChargeItems(chargeItemIds, patientId = null, existingTransaction = false) {
    let transaction = existingTransaction || [];
    const searchPatientId = patientId;

    if (!searchPatientId) {
        return transaction;
    }

    // Search by patient(s)
    const response = await $httpFhirApi.get(`BN_AuthRegister?patient=${searchPatientId}`);

    const authRegisters = extractAuthRegisters(response);

    // Filter to only include auth registers that have ledger entries referencing our charge items
    const chargeItemIdsSet = new Set(Array.isArray(chargeItemIds) ? chargeItemIds : [chargeItemIds]);

    const filteredAuthRegisters = authRegisters.filter((authRegister) => {
        if (!authRegister.ledger || !Array.isArray(authRegister.ledger)) {
            return false;
        }

        // Check if any ledger entry references one of our charge items
        const hasMatchingChargeItem = authRegister.ledger.some((ledgerItem) => {
            const chargeItemRef = ledgerItem.chargeItem;
            if (!chargeItemRef) {
                return false;
            }

            const chargeItemId = chargeItemRef.id || chargeItemRef.reference?.split('/').pop();
            const matches = chargeItemIdsSet.has(chargeItemId);
            return matches;
        });

        return hasMatchingChargeItem;
    });

    // For each auth register, remove ledger entries for the deleted charge items
    for (const authRegister of filteredAuthRegisters) {
        const updatedLedger = authRegister.ledger.filter((ledgerItem) => {
            const chargeItemRef = ledgerItem.chargeItem;
            if (!chargeItemRef) {
                return true;
            }

            const chargeItemId = chargeItemRef.id || chargeItemRef.reference?.split('/').pop();
            return !chargeItemIdsSet.has(chargeItemId);
        });

        // If no ledger entries remain, delete the entire auth register
        if (updatedLedger.length === 0) {
            transaction.push(crud.delete(authRegister));
        } else {
            authRegister.ledger = updatedLedger;

            let totalsObj = {
                baseUnit: undefined,
                baseAmount: undefined,
                currentUnit: 0,
                currentAmount: 0,
            };

            updatedLedger.forEach((ledgerItem) => {
                if (ledgerItem.chargeItem) {
                    if (ledgerItem.unit) {
                        totalsObj.currentUnit = (totalsObj.currentUnit || 0) + ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.currentAmount = (totalsObj.currentAmount || 0) + ledgerItem.amount?.value;
                    }
                } else {
                    if (ledgerItem.unit) {
                        totalsObj.baseUnit = ledgerItem.unit;
                        totalsObj.currentUnit = ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.baseAmount = ledgerItem.amount?.value;
                        totalsObj.currentAmount = ledgerItem.amount?.value;
                    }
                }
            });

            authRegister.exhausted = isAuthRegisterExhausted(totalsObj);

            // Use PUT (full-resource replace) rather than PATCH here. crud.patch
            // routes through fast-json-patch.compare(), which emits successive
            // `remove /ledger/N` ops at pre-shift indices; JSON Patch semantics
            // shift indices on every remove, so Aidbox 422s with "Nothing to
            // remove @ /ledger/N" whenever ≥2 entries on the same register are
            // filtered out in one call. PUT bypasses the diff entirely. (#4258)
            transaction.push(crud.put(authRegister));
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

export function useAuthFlow() {
    return {
        getChargeItemAuths,
        calculateAuths,
        removeAuthTrackingForChargeItems,
    };
}
